/**
 * [P0-FIX] TimerCentral — 全局定时器中枢调度器
 * 
 * 解决问题：imStore.ts 中 15+ 个散落 setInterval 同时激活，
 * 导致 IPC 通道过载 + 主线程调度风暴 + 页面不可见时无意义消耗。
 * 
 * 核心能力：
 * 1. 统一注册/注销所有定时器
 * 2. 页面不可见时自动暂停非关键定时器
 * 3. 系统高负载时自动降频（背压保护）
 * 4. 诊断接口用于运行时审计
 * 5. `interval()`：迁移用句柄入口（自动分配唯一名字，返回可清理句柄）——
 *    把散落的裸 `setInterval` 一批收进来的**低风险**通道
 * 6. `subscribe()`：按 key 共享一个 interval（同一接口被多个组件轮询时只留一条）
 */

export type TimerPriority = 'critical' | 'normal' | 'low';

/** `interval()` 返回的句柄：`clear()` 后不再执行，可安全重复调用。 */
export interface TimerHandle {
    readonly name: string;
    clear: () => void;
}

interface TimerEntry {
    id: ReturnType<typeof setInterval>;
    fn: () => void;
    intervalMs: number;
    priority: TimerPriority;
    paused: boolean;
    /** 页面不可见时是否暂停 */
    pauseOnHidden: boolean;
    /** 注册时间戳 */
    registeredAt: number;
    /** 累计执行次数 */
    execCount: number;
}

class TimerCentralImpl {
    private timers = new Map<string, TimerEntry>();
    private _visibility: DocumentVisibilityState = 'visible';
    private _backpressure = false;
    /** `interval()` 用的自增序号：保证自动名字唯一（同一组件多实例不互相覆盖）。 */
    private _autoSeq = 0;
    /** `subscribe()` 的共享条目：一个 key 一条 interval，广播给全部订阅者。 */
    private shared = new Map<string, { subs: Set<() => void>; handle: TimerHandle }>();

    constructor() {
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                // [2026-09-24] 只认 `visibilityState` 这一个来源：回调里的"双保险"读的是
                // `this._visibility`，而这里原先读 `document.hidden` —— 两个来源在真实浏览器里
                // 恒等，但**两个 guard 读两个来源**是可以在环境差异下分叉的（实测：测试桩只设
                // `visibilityState` 时，"真的 clearInterval"那条路不会走，退化成一个 50ms 定时器
                // 每拍醒来再早退）。单一来源让两条 guard 不可能说两套话。
                this._visibility = document.visibilityState;
                if (this._visibility === 'hidden') {
                    this._pauseEligible();
                } else {
                    this._resumeAll();
                }
            });
        }
    }

    /**
     * 注册定时器（如果同名已存在则先清理）
     */
    register(
        name: string,
        fn: () => void,
        intervalMs: number,
        opts?: {
            priority?: TimerPriority;
            pauseOnHidden?: boolean;
        }
    ): void {
        this.clear(name);
        const priority = opts?.priority ?? 'normal';
        const pauseOnHidden = opts?.pauseOnHidden ?? (priority !== 'critical');
        const entry: TimerEntry = {
            id: setInterval(() => {
                // 背压保护：high load 时 low 级别直接跳过
                if (this._backpressure && priority === 'low') return;
                // 页面不可见时非关键定时器已被暂停，此处是双保险
                if (pauseOnHidden && this._visibility === 'hidden') return;
                entry.execCount++;
                fn();
            }, intervalMs),
            fn,
            intervalMs,
            priority,
            paused: false,
            pauseOnHidden,
            registeredAt: Date.now(),
            execCount: 0,
        };
        this.timers.set(name, entry);
    }

    /**
     * 清理单个定时器
     */
    clear(name: string): void {
        const entry = this.timers.get(name);
        if (entry) {
            clearInterval(entry.id);
            this.timers.delete(name);
        }
    }

    /**
     * 迁移入口：自动命名 + 返回可清理句柄。
     *
     * 语义与 `register(name, …)` 完全相同，唯一区别是**名字由中枢生成**，
     * 因此同一组件被挂载多次时不会互相覆盖（裸 `register` 用固定名字会）。
     *
     * 迁移对照（纯机械、行为等价）：
     * ```ts
     * // 旧：裸定时器，标签页切后台照跑
     * const t = setInterval(fn, 1000);
     * …
     * clearInterval(t);
     * // 新：不可见即暂停（本地动画/轮询都适用）
     * const t = timerCentral.interval(fn, 1000, { pauseOnHidden: true });
     * …
     * t.clear();
     * ```
     */
    interval(
        fn: () => void,
        intervalMs: number,
        opts?: { priority?: TimerPriority; pauseOnHidden?: boolean }
    ): TimerHandle {
        const name = `__auto_${++this._autoSeq}`;
        this.register(name, fn, intervalMs, opts);
        return {
            name,
            clear: () => this.clear(name),
        };
    }

    /**
     * 按 `key` 共享一条 interval：同一 key 的多个订阅者共用一次 tick，**不各自起表**。
     *
     * 用于「同一个接口被多个组件轮询」的场景（本仓 `/monitor/bundle` 曾有三个组件
     * 各以 400ms/400ms/3s 各打一遍）。第一个订阅者建表，最后一个退订时拆表。
     * 返回退订函数（幂等）。
     */
    subscribe(
        key: string,
        fn: () => void,
        intervalMs: number,
        opts?: { priority?: TimerPriority; pauseOnHidden?: boolean }
    ): () => void {
        let entry = this.shared.get(key);
        if (!entry) {
            const subs = new Set<() => void>();
            const handle = this.interval(() => {
                subs.forEach((s) => {
                    try {
                        s();
                    } catch {
                        // 单个订阅者抛错不能拖垮同 key 的其他人
                    }
                });
            }, intervalMs, opts);
            entry = { subs, handle };
            this.shared.set(key, entry);
        }
        entry.subs.add(fn);
        let active = true;
        return () => {
            if (!active) return;
            active = false;
            const cur = this.shared.get(key);
            if (!cur) return;
            cur.subs.delete(fn);
            if (cur.subs.size === 0) {
                cur.handle.clear();
                this.shared.delete(key);
            }
        };
    }

    /**
     * 暂停符合条件的定时器（页面不可见时调用）
     */
    private _pauseEligible(): void {
        this.timers.forEach((entry, _name) => {
            if (entry.pauseOnHidden && !entry.paused) {
                clearInterval(entry.id);
                entry.paused = true;
            }
        });
    }

    /**
     * 恢复所有被暂停的定时器（页面重新可见时调用）
     */
    private _resumeAll(): void {
        this.timers.forEach((entry, _name) => {
            if (entry.paused) {
                entry.id = setInterval(() => {
                    if (this._backpressure && entry.priority === 'low') return;
                    if (entry.pauseOnHidden && this._visibility === 'hidden') return;
                    entry.execCount++;
                    entry.fn();
                }, entry.intervalMs);
                entry.paused = false;
            }
        });
    }

    /**
     * 启用/禁用背压模式（系统高负载时外部调用）
     */
    setBackpressure(enabled: boolean): void {
        this._backpressure = enabled;
    }

    /**
     * 诊断：获取所有注册定时器的快照
     */
    diagnostics(): Array<{
        name: string;
        intervalMs: number;
        priority: TimerPriority;
        paused: boolean;
        execCount: number;
        age: string;
    }> {
        const now = Date.now();
        return Array.from(this.timers.entries()).map(([name, e]) => ({
            name,
            intervalMs: e.intervalMs,
            priority: e.priority,
            paused: e.paused,
            execCount: e.execCount,
            age: `${Math.round((now - e.registeredAt) / 1000)}s`,
        }));
    }

    /**
     * [P2-DEEP] 按优先级暂停定时器（Watchdog 联动用）
     */
    pauseByPriority(priority: TimerPriority): void {
        this.timers.forEach((entry) => {
            if (entry.priority === priority && !entry.paused) {
                clearInterval(entry.id);
                entry.paused = true;
            }
        });
    }

    /**
     * [P2-DEEP] 暂停所有定时器（Watchdog Level 2 全面降级）
     */
    pauseAll(): void {
        this.timers.forEach((entry) => {
            if (!entry.paused) {
                clearInterval(entry.id);
                entry.paused = true;
            }
        });
    }

    /**
     * [P2-DEEP] 按优先级恢复定时器（安全模式解除后调用）
     */
    resumeByPriority(priority: TimerPriority): void {
        this.timers.forEach((entry) => {
            if (entry.priority === priority && entry.paused) {
                entry.id = setInterval(() => {
                    if (this._backpressure && entry.priority === 'low') return;
                    if (entry.pauseOnHidden && this._visibility === 'hidden') return;
                    entry.execCount++;
                    entry.fn();
                }, entry.intervalMs);
                entry.paused = false;
            }
        });
    }

    /**
     * 销毁所有定时器（组件卸载时调用）
     */
    destroy(): void {
        this.timers.forEach((entry) => clearInterval(entry.id));
        this.timers.clear();
        // 共享条目本身的 interval 已在上面被 clear；这里只清映射，避免残留空条目。
        this.shared.clear();
    }
}

export const timerCentral = new TimerCentralImpl();
